import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10 } from "styled-components";
import React from "react";
import { Grid } from "~/features/ui/grid";
import { Media } from "~/features/ui/media";
import { Headline } from "~/features/ui/headline";
import { takeFirst } from "~/features/app/utils";
import { Link } from "~/features/ui/link";
import { Button } from "~/features/ui/button";
import { RichText } from "~/features/ui/rich-text";
import { VideoControls } from "~/features/ui/video/video-controls";
import { type Mode } from "~/features/ui/get-theme-style";
import { isLinkValid } from "~/features/ui/link/utils";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export type IntroHeadlineBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  hasSolidBackground?: boolean | null;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  mediaDesktop?: React.ReactNode;
  mediaMobile?: React.ReactNode;
  mediaPosition?: string | null;
  mode?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  text?: React.ReactNode;
  theme?: string | null;
};
export function IntroHeadlineBlockView({
  anchor,
  hasSolidBackground = false,
  hasTopDivider = true,
  headline,
  link,
  mediaDesktop,
  mediaMobile,
  mediaPosition = "right",
  mode = "light",
  sectionProps,
  text,
  theme = "white"
}: IntroHeadlineBlockViewProps) {
  const cta = takeFirst(link);
  const icon = getPlainLinkIcon(cta);
  const textCol = <_StyledDiv key="text-col" $_css={[{
    "gridColumn": "1 / -1",
    "display": "flex",
    "height": "100%",
    "flexDirection": "column",
    "alignItems": "flex-start",
    "gap": "var(--spacing-xl)",
    "@media (min-width: 1024px)": {
      "gridColumn": "span 6 / span 6",
      "paddingLeft": "var(--spacing-3xl)",
      "paddingRight": "var(--spacing-3xl)"
    }
  }]}>
      <_StyledDiv2 $_css2={[{
      "display": "block",
      "width": "100%",
      "@media (min-width: 1024px)": {
        "display": "none"
      }
    }]}>{mediaMobile}</_StyledDiv2>

      {headline && <_StyledDiv3>
          <_StyledHeadline type="h2" text={headline} />
        </_StyledDiv3>}

      <_StyledDiv4 $_css3={[{
      "position": "relative",
      "zIndex": "10",
      "display": "flex",
      "flex": "1 1 0%",
      "flexDirection": "column-reverse",
      "alignItems": "flex-start",
      "justifyContent": "flex-end",
      "gap": "var(--spacing-xl)",
      "@media (min-width: 1024px)": {
        "flexDirection": "column",
        "justifyContent": "space-between"
      }
    }]}>
        {isLinkValid(cta) && <Button as={Link} {...cta} icon={icon} variant="secondary" />}

        {text && <_StyledDiv5 $_css4={[{
        "fontSize": "1.8rem",
        "lineHeight": "1.5",
        "letterSpacing": "0.018rem",
        "fontWeight": "400",
        "color": "var(--text-primary)"
      }]}>{text}</_StyledDiv5>}
      </_StyledDiv4>
    </_StyledDiv>;
  const mediaCol = <_StyledDiv6 key="media-col" $_css5={[{
    "gridColumn": "span 6 / span 6",
    "display": "none",
    "@media (min-width: 1024px)": {
      "display": "block"
    }
  }]}>
      <_StyledDiv7>{mediaDesktop}</_StyledDiv7>
    </_StyledDiv6>;
  const columns = mediaPosition === "right" ? [textCol, mediaCol] : [mediaCol, textCol];
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css6={[{
    "paddingLeft": "var(--spacing-lg)",
    "paddingRight": "var(--spacing-lg)",
    "paddingTop": "var(--spacing-3xl)",
    "paddingBottom": "var(--spacing-3xl)",
    "@media (min-width: 1024px)": {
      "paddingLeft": "var(--spacing-3xl)",
      "paddingRight": "var(--spacing-3xl)"
    }
  }, hasSolidBackground && {
    "position": "relative"
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, theme === "grey" ? {
    "backgroundColor": "var(--bg-tertiary)"
  } : {
    "backgroundColor": "var(--bg-primary)"
  }]}>
      {/* With media, the row takes the image's height so the image is never cropped; text-only keeps the tall layout. */}
      <_StyledGrid $_css7={[!mediaDesktop && {
      "@media (min-width: 1024px)": {
        "minHeight": "650px"
      }
    }]}>{columns}</_StyledGrid>
    </_StyledSection>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-9b79mg-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-9b79mg-1"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-9b79mg-2"
})({
  "position": "relative",
  "zIndex": "10"
});
var _StyledHeadline = _styled4(Headline).withConfig({
  componentId: "sc-sf-9b79mg-3"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledDiv4 = _styled5("div").withConfig({
  componentId: "sc-sf-9b79mg-4"
})`${p => p.$_css3}`;
var _StyledDiv5 = _styled6("div").withConfig({
  componentId: "sc-sf-9b79mg-5"
})`${p => p.$_css4}`;
var _StyledDiv6 = _styled7("div").withConfig({
  componentId: "sc-sf-9b79mg-6"
})`${p => p.$_css5}`;
var _StyledDiv7 = _styled8("div").withConfig({
  componentId: "sc-sf-9b79mg-7"
})({
  "position": "relative",
  "height": "100%",
  "width": "100%"
});
var _StyledSection = _styled9("section").withConfig({
  componentId: "sc-sf-9b79mg-8"
})`${p => p.$_css6}`;
var _StyledGrid = /*#__PURE__*/_styled10(Grid).withConfig({
  displayName: "intro-headline___StyledGrid",
  componentId: "sc-1nc5b4v-0"
})(["", ""], p => p.$_css7);